import React, { useEffect, useRef, useState } from 'react';
import { LinkTrackingViewLayout, LinkTrackingViewProps } from './LinkTracking.view';
import { SettingsType, useSettings } from '../../../hooks/useSettings';
import { FormElementAction, Void } from '../../../models/basic-objects';
import {
  UpdateCustomBitlySettingsInput,
  SocialOAuthContext,
  UpdateWorkspaceSettingsInput,
  CustomBitlySettingsValuesInput,
  StartSocialOAuthMutationFn,
  GetBitlyAccountsLazyQueryHookResult,
  ConnectBitlyMutationFn,
  ReconnectBitlyMutationFn,
  RemoveBitlyMutationFn,
  UpdateWorkspaceSettingsMutationFn,
  UpdateCustomBitlySettingsMutationFn,
  SocialOAuthProvider
} from '../../../models/graphql';
import { GetBitlyAccounts_workspace_socialProfiles } from '../../../models/legacy-graphql';
import { useSelectedOrganization } from '../../../hooks/useSelectedOrganization';
import { useReactiveVar } from '@apollo/client';
import { ToggleAction } from '../../../components/forms/Toggle/Toggle.controller';
import { SelectItem } from '../../../components/forms/Select/Select.view';
import { AuthContextEnum } from '../../../constants/auth';
import { ISocialOAuth } from '../../../models/auth';
import { profilesHelpers } from '../Profiles/Profiles.helpers';
import { MappedProfileType } from '../../../components/socialProfiles/SocialProfileListItem/SocialProfileListItem.view';
import { triggerErrorToast } from '../../../helpers/triggerErrorToast';
import { showToast } from '../../../helpers/showToast';
import { socialOAuthVar } from '../../../providers/AppProvider';

export type UTMSettingsType = {
  campaignTracking: boolean;
  shouldRemoveUtm: boolean;
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
  utmTerm: string;
  utmContent: string;
};

export type ErrorModalType = {
  title: string;
  action: Void;
  content: JSX.Element;
};

export type LinkTrackingControllerProps = {
  getBitlyAccounts: GetBitlyAccountsLazyQueryHookResult[0];
  bitlyResults: GetBitlyAccountsLazyQueryHookResult[1];
  startSocialOAuth: StartSocialOAuthMutationFn;
  connectBitlyAccount: ConnectBitlyMutationFn;
  reconnectBitlyAccount: ReconnectBitlyMutationFn;
  removeBitlyAccount: RemoveBitlyMutationFn;
  updateWorkspaceSettings: UpdateWorkspaceSettingsMutationFn;
  updateCustomBitlySettings: UpdateCustomBitlySettingsMutationFn;
};

export const LinkTrackingController = (props: LinkTrackingControllerProps): JSX.Element => {
  const socialOAuth: ISocialOAuth | null = useReactiveVar(socialOAuthVar);
  const settings: SettingsType = useSettings();
  const { selectedWorkspace } = useSelectedOrganization();
  const [showCustomLinkTracking, setShowCustomLinkTracking] = useState<boolean>(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<boolean>(false);
  const [errorModal, setErrorModal] = useState<ErrorModalType | null>(null);
  const [showUtmEditModal, setShowUtmEditModal] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [customizePerProfileLoading, setCustomizePerProfileLoading] = useState<boolean>(false);
  const [bitlyAccountToDeleteId, setBitlyAccountToDeleteId] = useState<string>('');
  const connectedBitlyAccounts = props.bitlyResults.data?.workspace.bitlyAccounts.filter(b => b.connected);
  const disconnectedBitlyAccounts = props.bitlyResults.data?.workspace.bitlyAccounts.filter(b => !b.connected);

  // To ensure we only call the connect/reconnect mutation once.
  const connectReconnectCalled = useRef(false);

  // If not set, we always use our default values
  const utmSettings: UTMSettingsType = {
    campaignTracking: props.bitlyResults.data?.workspace.settings.campaignTracking || false,
    shouldRemoveUtm: props.bitlyResults.data?.workspace.settings.shouldRemoveUtm || false,
    utmSource: props.bitlyResults.data?.workspace.settings.utm?.source || 'NETWORK',
    utmMedium: props.bitlyResults.data?.workspace.settings.utm?.medium || 'social',
    utmCampaign: props.bitlyResults.data?.workspace.settings.utm?.campaign || 'ACCOUNT',
    utmTerm: props.bitlyResults.data?.workspace.settings.utm?.term || '',
    utmContent: props.bitlyResults.data?.workspace.settings.utm?.content || 'CATEGORY'
  };

  // Performs connect or reconnect handling of bitly accounts
  const bitlyConnectButtonHandler = (isReconnect?: boolean, profile?: MappedProfileType): void => {
    const startOAuth = {
      setLoading: setLoading,
      oAuthProvider: SocialOAuthProvider.BITLY,
      context: isReconnect ? AuthContextEnum.reconnectProfile : AuthContextEnum.connectProfile,
      startSocialOAuth: props.startSocialOAuth,
      socialOAuthContext: isReconnect ? SocialOAuthContext.RECONNECT : SocialOAuthContext.CONNECT,
      ...(isReconnect && profile ? { profile } : {})
    };
    profilesHelpers.setNetworkAndCallOAuth(startOAuth);
  };

  // Handler for the toggle that enables/disables Bitly customisations
  const customLinkTrackingToggleHandler: ToggleAction = (value, complete) => {
    const inputs: UpdateCustomBitlySettingsInput[] = [];

    if (value) {
      setCustomizePerProfileLoading(true);
    }

    props.bitlyResults.data?.workspace.socialProfiles.forEach(p => {
      if (!value) {
        inputs.push({ socialProfileId: p.id, bitly: null });
      } else {
        inputs.push({
          socialProfileId: p.id,
          bitly: getBitlyInputFromDefaultBitly()
        });
      }
    });

    bulkUpdateCustomBitlySettings(inputs, complete);
    setShowCustomLinkTracking(value);
  };

  // manages the conversion of the default value mapping to customisation
  const getBitlyInputFromDefaultBitly = (): CustomBitlySettingsValuesInput => ({
    bitlyId: props.bitlyResults.data?.workspace.settings.defaultBitly?.bitly.id || null,
    domain: props.bitlyResults.data?.workspace.settings.defaultBitly?.domain || 'none'
  });

  const defaultBitlyDomainSelectHandler: FormElementAction = (value, complete) => {
    const data: CustomBitlySettingsValuesInput = typeof value === 'string' ? JSON.parse(value) : {};

    const input: UpdateWorkspaceSettingsInput = {
      bitly: data.bitlyId ? data : null
    };

    updateWorkspaceSettings(input, 'Error updating your default Bitly settings', complete);
  };

  // A Handler for the custom Bitly dropdown/selects
  const customBitlyDomainSelectHandler: FormElementAction = (value, complete) => {
    const input: UpdateCustomBitlySettingsInput = typeof value === 'string' ? JSON.parse(value) : {};

    bulkUpdateCustomBitlySettings([input], complete);
  };

  // Use one method for mutation call to update/delete custom Bitly
  const bulkUpdateCustomBitlySettings = (
    inputs: UpdateCustomBitlySettingsInput[],
    complete?: (keepNewVal?: boolean | undefined) => void
  ) => {
    if (selectedWorkspace) {
      props
        .updateCustomBitlySettings({
          variables: {
            input: inputs
          }
        })
        .catch(triggerErrorToast('Error updating your custom Bitly settings'))
        .finally(() => {
          if (complete) {
            complete(true);
          }

          setCustomizePerProfileLoading(false);
        });
    }
  };

  // Handler for the UTM Parameter that enables/disables removal of the UTM URL
  const removeUTMParametersToggleHandler: ToggleAction = (value, complete) => {
    const input: UpdateWorkspaceSettingsInput = {
      removeUtm: value
    };

    updateWorkspaceSettings(
      input,
      value ? 'Error enabling UTM Parameter removal' : 'Error disabling UTM Parameter removal',
      complete
    );
  };

  // Handler for the custom UTM settings table to show or hide
  const customUtmSettingsToggleHandler: ToggleAction = (value, complete) => {
    const input: UpdateWorkspaceSettingsInput = {
      campaignTracking: value
    };

    updateWorkspaceSettings(
      input,
      value ? 'Error enabling custom UTM Settings' : 'Error disabling custom UTM Settings',
      complete
    );
  };

  // A single method to handle graphql mutations for workspaces settings used by the UTM form elements
  const updateWorkspaceSettings = (
    input: UpdateWorkspaceSettingsInput,
    toastErrorTitle: string,
    complete: (keepNewVal?: boolean | undefined) => void
  ) => {
    if (selectedWorkspace) {
      props
        .updateWorkspaceSettings({
          variables: {
            workspaceId: selectedWorkspace.id,
            input: input
          }
        })
        .catch(triggerErrorToast(toastErrorTitle))
        .finally(() => {
          complete(true);
        });
    }
  };

  // Takes the workspace default bitly value, maps it to the input type to update the workspace default bitly,
  // and stringifies the object to use as the select form value.
  const getDefaultBitlyInputValue = (): CustomBitlySettingsValuesInput => ({
    bitlyId: props.bitlyResults.data?.workspace.settings.defaultBitly?.bitly.id || null,
    domain: props.bitlyResults.data?.workspace.settings.defaultBitly?.domain || 'none'
  });

  // Gets the bitly value of the given profile if it exists
  // This also handles the default form value to decrease prop passing
  const getCustomBitlySettingsValueInput = (profileId: string): UpdateCustomBitlySettingsInput => {
    const profile = props.bitlyResults.data?.workspace.socialProfiles.find(p => p.id === profileId);
    // We set to default values and only update if a custom value has previously been set.
    let selectValue: UpdateCustomBitlySettingsInput = {
      socialProfileId: profileId,
      bitly: {
        bitlyId: null,
        domain: 'none'
      }
    };

    if (profile?.customBitly && profile.customBitly.domain?.length) {
      const bitlyId = profile.customBitly.domain !== 'none' ? profile.customBitly?.bitly?.id || null : null;
      selectValue = {
        socialProfileId: profileId,
        bitly: {
          bitlyId: bitlyId,
          domain: profile.customBitly.domain || ''
        }
      };
    }

    return selectValue;
  };

  const addDefaultBitlyToNewSocialProfiles = (socialProfiles: GetBitlyAccounts_workspace_socialProfiles[]) => {
    const inputs: UpdateCustomBitlySettingsInput[] = [];
    const hasCustomisedBitly = socialProfiles.find(profile => profile.customBitly !== null);
    const bitly = getBitlyInputFromDefaultBitly();

    // Do not attempt to change anything if customisation has not already been established
    if (!hasCustomisedBitly) {
      return;
    }

    socialProfiles.forEach(profile => {
      if (!profile.customBitly) {
        inputs.push({
          socialProfileId: profile.id,
          bitly: bitly
        });
      }
    });

    // Only process if we have something
    if (inputs.length > 0) {
      bulkUpdateCustomBitlySettings(inputs);
    }
  };

  const getDefaultBitlyDomainListSelectItems = (): Array<SelectItem> => {
    // We have two difference structures for the default and customised tracking
    // The default bitly setting will not have a profile ID
    const noneValue: CustomBitlySettingsValuesInput = {
      bitlyId: null,
      domain: 'none'
    };

    const domainList: Array<SelectItem> = [{ label: 'None', value: JSON.stringify(noneValue) }];

    props.bitlyResults.data?.workspace.bitlyAccounts.forEach(account => {
      account.domains.forEach(domain => {
        const selectValues: CustomBitlySettingsValuesInput = {
          bitlyId: account.id,
          domain: domain
        };
        const valueString = JSON.stringify(selectValues);

        domainList.push({ value: valueString, label: `${account.displayName}/${domain}` });
      });
    });

    return domainList;
  };

  // Returns a list of SelectItems with appropriate json string value
  const getBitlyDomainListSelectItems = (profileId: string): Array<SelectItem> => {
    // We have two difference structures for the default and customised tracking
    // The default bitly setting will not have a profile ID
    const noneValue = {
      socialProfileId: profileId,
      bitly: {
        bitlyId: null,
        domain: 'none'
      }
    };

    const domainList: Array<SelectItem> = [{ label: 'None', value: JSON.stringify(noneValue) }];

    props.bitlyResults.data?.workspace.bitlyAccounts.forEach(account => {
      account.domains.forEach(domain => {
        const selectValues: UpdateCustomBitlySettingsInput | CustomBitlySettingsValuesInput = profileId
          ? {
              socialProfileId: profileId,
              bitly: {
                bitlyId: account.id,
                domain: domain
              }
            }
          : {
              bitlyId: account.id,
              domain: domain
            };
        const valueString = JSON.stringify(selectValues);

        domainList.push({ value: valueString, label: `${account.displayName}/${domain}` });
      });
    });

    return domainList;
  };

  // This is called on the return from bitly for any NEW connection
  const connectyBitlyAfterOAuth = async (socialOAuth: ISocialOAuth): Promise<void> => {
    if (selectedWorkspace?.id) {
      props
        .connectBitlyAccount({
          variables: {
            workspaceId: selectedWorkspace.id,
            socialAuthToken: socialOAuth.socialAuthToken
          }
        })
        .then(() => {
          showToast({
            summary: 'Account Connected',
            detail: 'Bitly Account connected successfully.',
            severity: 'success'
          });
        })
        .catch(e => {
          if (e.networkError.result?.errors.find((e: { message: string }) => e.message === 'ALREADY_EXISTS')) {
            setErrorModal({
              title: 'Connect another account',
              action: () => bitlyConnectButtonHandler(false),
              content: (
                <>
                  You are trying to add a Bitly account that&apos;s already connected to your SmarterQueue account. If
                  you wish to add another Bitly account instead, please first log in with that account on Bitly in this
                  browser and try again.
                </>
              )
            });
          } else {
            triggerErrorToast('Bitly Account Connection Failed')(e);
          }
        })
        .finally(() => {
          socialOAuthVar(null);
        });
    }
  };

  // This is called on return from bitly for any RECONNECT
  const reconnectBitlyAfterOAuth = async (socialOAuth: ISocialOAuth): Promise<void> => {
    if (selectedWorkspace?.id && socialOAuth.profile) {
      props
        .reconnectBitlyAccount({
          variables: {
            bitlyId: socialOAuth.profile?.id,
            socialAuthToken: socialOAuth.socialAuthToken
          }
        })
        .then(() => {
          showToast({
            summary: 'Account Re-connected',
            detail: 'Bitly Account re-connected successfully.',
            severity: 'success'
          });
        })
        .catch(e => {
          if (
            e.networkError.result.errors.find(
              (e: { message: string }) => e.message === 'SOCIAL_PROFILE_RECONNECT_MISMATCH'
            )
          ) {
            setErrorModal({
              title: 'Wrong Bitly Account',
              action: () => bitlyConnectButtonHandler(true, socialOAuth.profile),
              content: (
                <>
                  We could not re-connect your &apos;{socialOAuth.profile?.displayName}&apos; Bitly account, as
                  you&apos;re currently logged into a different Bitly account in this browser. If you wish to re-connect
                  your &apos;
                  {socialOAuth.profile?.displayName}&apos; account instead, first make sure you&apos;re logged into that
                  account at Bitly.com, and try again.
                </>
              )
            });
          } else {
            triggerErrorToast('Account reconnection failed');
          }
        })
        .finally(() => {
          socialOAuthVar(null);
        });
    }
  };

  // A handler to remove bitly accounts
  const removeBitlyAccountHandler = (bitlyId: string, confirmed?: boolean): void => {
    if (!confirmed) {
      setBitlyAccountToDeleteId(bitlyId);
      setShowDeleteConfirmation(true);
    }

    if (selectedWorkspace?.id && confirmed) {
      props
        .removeBitlyAccount({
          variables: {
            bitlyId: bitlyId
          },
          update: cache => {
            cache.evict({ id: cache.identify({ id: bitlyId, __typename: 'Bitly' }) });
          }
        })
        .catch(triggerErrorToast('Bitly Delete Error'))
        .finally(() => {
          setBitlyAccountToDeleteId('');
          setShowDeleteConfirmation(false);
        });
    }
  };

  const sortedSocialProfiles: GetBitlyAccounts_workspace_socialProfiles[] = [
    ...(props.bitlyResults.data?.workspace.socialProfiles || [])
  ]?.sort((a, b) => +a.id - +b.id);

  // used to determine if customisation has occurred
  useEffect(() => {
    props.bitlyResults.data?.workspace.socialProfiles.forEach(p => {
      if (p.customBitly) {
        setShowCustomLinkTracking(true);
      }
    });
  }, [selectedWorkspace, props.bitlyResults.data?.workspace.socialProfiles]);

  // This is used for handling the Bitly (re)Connect callbacks
  useEffect(() => {
    if (socialOAuth?.context === AuthContextEnum.connectProfile && !connectReconnectCalled.current) {
      connectReconnectCalled.current = true;
      connectyBitlyAfterOAuth(socialOAuth).catch(() => {
        // catch runtime error
      });
    } else if (socialOAuth?.context === AuthContextEnum.reconnectProfile && !connectReconnectCalled.current) {
      connectReconnectCalled.current = true;
      reconnectBitlyAfterOAuth(socialOAuth).catch(() => {
        // catch runtime error
      });
    } else {
      socialOAuthVar(null);
    }
    // TODO-FIX-TS-WARNINGS
    // eslint-disable-next-line
  }, [selectedWorkspace, socialOAuth]);

  // calls the query for when the workspace has changed
  useEffect(() => {
    if (selectedWorkspace) {
      props.getBitlyAccounts({
        variables: { workspaceId: selectedWorkspace.id }
      });
    }
    // TODO-FIX-TS-WARNINGS
    // eslint-disable-next-line
  }, [selectedWorkspace]);

  // Determines if there is an active loading state and applies it to state used in the view
  useEffect(() => {
    setLoading(!props.bitlyResults.called || props.bitlyResults.loading);
  }, [props.bitlyResults]);

  // Make sure all social profiles have defaultBitly if its currently NULL
  useEffect(() => {
    addDefaultBitlyToNewSocialProfiles(props.bitlyResults.data?.workspace.socialProfiles || []);
    // TODO-FIX-TS-WARNINGS
    // eslint-disable-next-line
  }, [props.bitlyResults.data?.workspace.socialProfiles]);

  const viewProps: LinkTrackingViewProps = {
    ...props,
    customizePerProfileLoading,
    loading: loading,
    bitlyAccounts: props.bitlyResults.data?.workspace.bitlyAccounts || [],
    socialProfiles: sortedSocialProfiles,
    connectedBitlyAccounts: connectedBitlyAccounts || [],
    disconnectedBitlyAccounts: disconnectedBitlyAccounts || [],
    settings,
    errorModal,
    setErrorModal,
    showDeleteConfirmation,
    setShowDeleteConfirmation,
    bitlyAccountToDeleteId,
    showCustomLinkTracking,
    setShowCustomLinkTracking,
    showUtmEditModal,
    setShowUtmEditModal,
    utmSettings,
    bitlyConnectButtonHandler,
    getDefaultBitlyDomainListSelectItems,
    getBitlyDomainListSelectItems,
    getDefaultBitlyInputValue,
    getCustomBitlySettingsValueInput,
    customBitlyDomainSelectHandler,
    defaultBitlyDomainSelectHandler,
    customUtmSettingsToggleHandler,
    removeUTMParametersToggleHandler,
    customLinkTrackingToggleHandler,
    removeBitlyAccountHandler
  };
  return <LinkTrackingViewLayout {...viewProps} />;
};
